import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'api/httpClient';
import { config } from 'config';
import * as actions from 'redux/actions/sessionActions';
import { Translate, I18n } from 'react-redux-i18n';
import { AvatarOrInitials } from 'utils/avatarUtils';

const mapCountryOption = (item) => ({
  code: item.Prefix || item.prefix || '',
  label: item.Name || item.name || '',
});

const AccountDetails = (props) => {
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [values, setValues] = useState({
    id: props.user_id,
    name: props.item.name || '',
    lastN: props.item.lastN || '',
    email: props.item.email || '',
    phone: props.item.phone || '',
    country: props.item.country || '',
    username: props.item.username || '',
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');
  const avatarInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadCountries = async () => {
      setCountriesLoading(true);
      try {
        const response = await axios.get(`${config.api.web}countries/actives`, {
          withCredentials: true,
        });

        if (cancelled) {
          return;
        }

        if (response.data?.status === 'Success') {
          const list = response.data.body?.list || [];
          setCountries(
            list
              .map(mapCountryOption)
              .filter((option) => option.code && option.label)
          );
        }
      } catch (error) {
        if (!cancelled) {
          setCountries([]);
        }
      } finally {
        if (!cancelled) {
          setCountriesLoading(false);
        }
      }
    };

    loadCountries();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setValues({
      id: props.user_id,
      name: props.item.name || '',
      lastN: props.item.lastN || '',
      email: props.item.email || '',
      phone: props.item.phone || '',
      country: props.item.country || '',
      username: props.item.username || '',
    });
  }, [props.item, props.user_id]);

  useEffect(() => {
    if (props.item.avatar) {
      setAvatarPreview('');
    }
  }, [props.item.avatar]);

  useEffect(() => () => {
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }
  }, [avatarPreview]);

  const handleChange = (event) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value,
    });
  };

  const onSave = (event) => {
    event.preventDefault();
    props.updateProfile(values, props.token);
  };

  const onAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setAvatarUploading(true);
    const result = await props.uploadAvatar(file);
    setAvatarUploading(false);
    if (!result?.success) {
      URL.revokeObjectURL(previewUrl);
      setAvatarPreview('');
    }
  };

  return (
    <div className="account-panel">
      <h2 className="account-panel-title">
        <Translate value="profile" />
      </h2>
      <p className="account-panel-subtitle">
        <Translate value="profile_detail" />
      </p>

      <form autoComplete="on" onSubmit={onSave}>
        <div className="account-profile-layout">
          <div className="account-avatar-wrap">
            <div className="account-avatar-inner">
              <AvatarOrInitials
                avatarUrl={avatarPreview || props.item.avatar}
                name={values.name}
                lastN={values.lastN}
                emailFallback={values.email}
                className="account-avatar"
                alt={I18n.t('profile')}
              />
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="account-avatar-file-input"
                onChange={onAvatarChange}
                aria-hidden="true"
                tabIndex={-1}
              />
              <button
                type="button"
                className="account-avatar-edit-btn"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarUploading}
              >
                {avatarUploading ? '...' : <Translate value="upload" />}
              </button>
            </div>
          </div>

          <div className="account-fields">
            <div className="account-field">
              <label htmlFor="account-name">
                <Translate value="first_name" />
              </label>
              <input
                id="account-name"
                name="name"
                autoComplete="given-name"
                onChange={handleChange}
                required
                value={values.name}
              />
            </div>
            <div className="account-field">
              <label htmlFor="account-lastN">
                <Translate value="last_name" />
              </label>
              <input
                id="account-lastN"
                name="lastN"
                autoComplete="family-name"
                onChange={handleChange}
                required
                value={values.lastN}
              />
            </div>
            <div className="account-field">
              <label htmlFor="account-email">Email</label>
              <input
                id="account-email"
                name="email"
                type="email"
                autoComplete="email"
                onChange={handleChange}
                required
                value={values.email}
              />
            </div>
            <div className="account-field">
              <label htmlFor="account-phone">
                <Translate value="phone" />
              </label>
              <input
                id="account-phone"
                name="phone"
                type="tel"
                onChange={handleChange}
                value={values.phone}
              />
            </div>
            <div className="account-field">
              <label htmlFor="account-username">
                <Translate value="username" />
              </label>
              <input
                id="account-username"
                name="username"
                type="text"
                onChange={handleChange}
                value={values.username}
              />
            </div>
            <div className="account-field">
              <label htmlFor="account-country">
                <Translate value="country" />
              </label>
              <select
                id="account-country"
                name="country"
                value={values.country}
                onChange={handleChange}
                disabled={countriesLoading}
              >
                <option value="">{I18n.t('select') || '—'}</option>
                {values.country
                  && !countries.some((option) => option.code === values.country) && (
                  <option value={values.country}>{values.country}</option>
                )}
                {countries.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="account-actions">
              <button type="submit" className="btn btn-primary">
                <Translate value="save" />
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

AccountDetails.propTypes = {
  className: PropTypes.string,
};

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.token,
    item: reducer.session.auth,
  };
}

const mapDispatchToProps = (dispatch) => ({
  updateProfile: (params, token) => dispatch(actions.updateItem({ params, token })),
  uploadAvatar: (file) => dispatch(actions.uploadAvatar(file)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountDetails);
